/**
 * Spotlight — Cmd+K global search (RunAI tarzı)
 * - Apps (DOCK_ITEMS)
 * - Agents (API: /api/agents)
 * - Komutlar (logout, settings, theme toggle)
 * Klavye: ↑↓ navigate, Enter aç, Esc kapat
 */
import { Bot, Command, Grid3x3, LogOut, Search, Settings, Zap } from 'lucide-react'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { API_URL } from '../../config/api'
import { DOCK_ITEMS } from './Dock/dockConfig'
import { useWindowManager } from './WindowManager/WindowManagerContext'
import { WINDOW_REGISTRY } from './WindowManager/windowRegistry'

interface Hit {
  id: string
  type: 'app' | 'agent' | 'action'
  title: string
  subtitle?: string
  color: string
  icon: React.ReactNode
  run: () => void
}

interface Props {
  open: boolean
  onClose: () => void
}

export default function Spotlight({ open, onClose }: Props) {
  const { openWindow, windows } = useWindowManager()
  const [q, setQ] = useState('')
  const [idx, setIdx] = useState(0)
  const [agents, setAgents] = useState<Array<{ id: number; agent_name: string; expertise?: string }>>([])
  const [skills, setSkills] = useState<Array<{ id: number; name: string; description?: string }>>([])
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setQ(''); setIdx(0)
      setTimeout(() => inputRef.current?.focus(), 30)
    }
  }, [open])

  useEffect(() => {
    if (!open || (agents.length > 0 && skills.length > 0)) return
    const token = localStorage.getItem('token') ?? ''

    if (agents.length === 0) {
      fetch(`${API_URL}/api/agents`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          const list = Array.isArray(d) ? d : (d?.agents ?? d?.results ?? [])
          setAgents(list.slice(0, 50))
        })
        .catch(() => setAgents([]))
    }

    if (skills.length === 0) {
      fetch(`${API_URL}/api/skills`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          const list = Array.isArray(d?.skills) ? d.skills : []
          setSkills(list.slice(0, 30))
        })
        .catch(() => setSkills([]))
    }
  }, [open, agents.length, skills.length])

  const launchApp = (path: string, id: string, title: string, w = 860, h = 580, noPadding = false) => {
    const Comp = WINDOW_REGISTRY[path]
    if (!Comp) return
    const vw = window.innerWidth, vh = window.innerHeight
    const W = Math.min(w, Math.floor(vw * 0.85))
    const H = Math.min(h, Math.floor(vh * 0.80))
    const openCount = windows.length
    const x = Math.max(20, Math.floor((vw - W) / 2) + openCount * 20)
    const y = Math.max(20, Math.floor((vh - H) / 2) + openCount * 20)
    const Wrapped = () => (
      <Suspense fallback={<div style={{ padding: 40, color: 'var(--os-muted)', fontSize: 13 }}>Loading...</div>}>
        <Comp />
      </Suspense>
    )
    openWindow({ id, title, component: Wrapped, position: { x, y }, size: { width: W, height: H }, noPadding })
    onClose()
  }

  const hits: Hit[] = useMemo(() => {
    const all: Hit[] = []
    const apps = DOCK_ITEMS.filter(i => !i.separator && i.path)
    apps.forEach(a => {
      const Icon = a.icon
      all.push({
        id: `app-${a.id}`, type: 'app', title: a.label,
        subtitle: 'Application', color: a.color || '#6366f1',
        icon: <Icon size={16} color="#fff" />,
        run: () => launchApp(a.path, a.id, a.windowTitle ?? a.label, a.windowWidth, a.windowHeight, a.noPadding),
      })
    })
    agents.forEach(ag => {
      all.push({
        id: `agent-${ag.id}`, type: 'agent', title: ag.agent_name,
        subtitle: ag.expertise ?? `Agent #${ag.id}`, color: '#ec4899',
        icon: <Bot size={16} color="#fff" />,
        run: () => { window.location.href = `/agent/${ag.id}`; onClose() },
      })
    })
    skills.forEach(sk => {
      all.push({
        id: `skill-${sk.id}`, type: 'action', title: sk.name,
        subtitle: sk.description ?? 'Skill', color: '#f59e0b',
        icon: <Zap size={16} color="#fff" />,
        run: () => {
          onClose()
          window.dispatchEvent(new CustomEvent('runai:skill-run', { detail: { skillId: sk.id, skillName: sk.name } }))
        },
      })
    })
    all.push({
      id: 'act-pad', type: 'action', title: 'Open RunaiPad',
      subtitle: 'All applications', color: '#6366f1',
      icon: <Grid3x3 size={16} color="#fff" />,
      run: () => launchApp('runai-pad', 'runai-pad', 'RunaiPad', 1100, 700, true),
    })
    all.push({
      id: 'act-settings', type: 'action', title: 'Settings',
      subtitle: 'Theme, dock, profile', color: 'var(--os-muted)',
      icon: <Settings size={16} color="#fff" />,
      run: () => { window.location.href = '/dashboard/settings'; onClose() },
    })
    all.push({
      id: 'act-logout', type: 'action', title: 'Logout',
      subtitle: 'Sign out', color: '#ef4444',
      icon: <LogOut size={16} color="#fff" />,
      run: () => { localStorage.removeItem('token'); window.location.href = '/login' },
    })

    if (!q.trim()) return all.slice(0, 30)
    const t = q.trim().toLowerCase()
    return all.filter(h => h.title.toLowerCase().includes(t) || (h.subtitle ?? '').toLowerCase().includes(t)).slice(0, 50)
  }, [q, agents, skills, windows.length])

  useEffect(() => { setIdx(0) }, [q])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose() }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, hits.length - 1)) }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)) }
      else if (e.key === 'Enter') { e.preventDefault(); hits[idx]?.run() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, hits, idx, onClose])

  if (!open) return null

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'var(--os-glass-overlay)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '14vh',
        animation: 'spotIn .15s ease-out',
      }}
    >
      <style>{`@keyframes spotIn{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}}`}</style>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 620,
          background: 'var(--os-surface)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          border: '1px solid var(--os-glass-border)',
          borderRadius: 14,
          boxShadow: 'var(--os-card-shadow)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          maxHeight: '70vh',
        }}
      >
        {/* Search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 18px',
          borderBottom: '1px solid var(--os-divider)',
        }}>
          <Search size={18} color="var(--os-muted)" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search apps, agents, commands..."
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--os-text)', fontSize: 16, fontFamily: 'inherit',
            }}
          />
          <style>{`
            [data-area="os"] input::placeholder { color: var(--os-muted); opacity: 0.7; }
          `}</style>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--os-dim)', fontSize: 11, fontFamily: 'monospace' }}>
            <Command size={11} /> K
          </span>
        </div>

        {/* Results */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {hits.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--os-dim)', fontSize: 13 }}>
              No results
            </div>
          ) : (
            hits.map((h, i) => (
              <button
                key={h.id}
                onClick={() => h.run()}
                onMouseEnter={() => setIdx(i)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 18px',
                  background: i === idx ? 'var(--os-hover)' : 'transparent',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: `linear-gradient(135deg, ${h.color}cc, ${h.color}88)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {h.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--os-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.title}</div>
                  {h.subtitle && (
                    <div style={{ fontSize: 11, color: 'var(--os-muted)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.subtitle}</div>
                  )}
                </div>
                <span style={{
                  fontSize: 9, color: 'rgba(255,255,255,0.4)',
                  textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
                  padding: '2px 6px', borderRadius: 4,
                  background: 'rgba(255,255,255,0.05)',
                  flexShrink: 0,
                }}>
                  {h.type === 'app' ? 'app' : h.type === 'agent' ? 'agent' : 'cmd'}
                </span>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          padding: '8px 16px',
          borderTop: '1px solid var(--os-divider)',
          fontSize: 10, color: 'var(--os-muted)',
        }}>
          <span>{hits.length} results</span>
          <span><Zap size={9} style={{ display: 'inline', marginRight: 3 }} />↑↓ Navigate · ⏎ Open · esc Close</span>
        </div>
      </div>
    </div>
  )
}
